import { createSlice, isRejected } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import {
  FINISHED_STATUSES,
  MAX_NOTIFICATIONS,
  NOTIFICATION_KINDS,
  NOTIFICATION_STATUS,
  OPEN_STATUSES,
  SILENT_NOTIFICATION_KINDS,
  STALLED_AFTER_MS,
  STATUS_MESSAGES,
} from './notificationsConstants';
import { notificationsPersistConfig } from './notificationsPersist';
import { describeEvent, describeStart, parseChannel } from './notificationsUtils';

/**
 * What the Centro de Actividad shows.
 *
 * `items` is persisted under its own key, so closing the tab no longer throws
 * away the report someone asked for ten minutes ago. The backend still has no
 * history endpoint: what comes back is a snapshot of what this browser happened
 * to be told, not what the server did. The one thing it must not do is lie
 * about a job still running — `notificationsPersist` is where a stored entry is
 * made honest again on the way in.
 *
 * `isConnected` is not persisted: the socket says that, not the disk.
 */
const initialState = {
  items: [],
  isConnected: false,
  /** In the store so a toast's "Ver" can open it from any page. Not persisted. */
  isPanelOpen: false,
};

let sequence = 0;

const createEntry = (kind, descriptor, { read, requestId = null }) => {
  sequence += 1;
  const now = Date.now();

  return {
    id: `${kind}-${now}-${sequence}`,
    kind,
    requestId,
    ...descriptor,
    createdAt: now,
    updatedAt: now,
    read,
  };
};

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    /**
     * Opens an entry from the click that started the job, so the panel and the
     * button's spinner react before the round trip through Celery and Redis.
     * Marked read on purpose: the user is looking at the action they just took.
     */
    notificationStarted: (state, action) => {
      // `requestId` is the thunk's: if its request fails, the rejection closes
      // this entry instead of leaving it spinning for good.
      const { kind, requestId = null } = action.payload;
      state.items.unshift(createEntry(kind, describeStart(kind), { read: true, requestId }));
      state.items = state.items.slice(0, MAX_NOTIFICATIONS);
    },

    notificationReceived: (state, action) => {
      const { channel, payload, role, userId } = action.payload;

      const kind = parseChannel(channel, role, userId);
      if (!kind) return;

      // Signals travel on the same socket but are not entries. Other slices
      // still see the action — this only keeps the panel out of it.
      if (SILENT_NOTIFICATION_KINDS.includes(kind)) return;

      const descriptor = describeEvent(kind, payload);

      // Progress arrives as a series — started, processing, completed — and it
      // is one job, so it gets one row. Matching on the kind is the only option
      // available: no producer sends a job id, so there is nothing else to
      // correlate on. Finished entries are never matched, which is what lets a
      // second export open its own row instead of overwriting the first; a
      // stalled or unconfirmed one is, because the event proves it is alive.
      const running = state.items.find(
        (item) => item.kind === kind && OPEN_STATUSES.includes(item.status)
      );

      if (!running) {
        state.items.unshift(createEntry(kind, descriptor, { read: false }));
        state.items = state.items.slice(0, MAX_NOTIFICATIONS);
        return;
      }

      Object.assign(running, descriptor, { updatedAt: Date.now() });

      // Reaching a terminal state is the news worth a badge; the intermediate
      // "processing" is not.
      if (descriptor.status !== NOTIFICATION_STATUS.RUNNING) {
        running.read = false;
      }
    },

    notificationsMarkAllRead: (state) => {
      state.items.forEach((item) => {
        item.read = true;
      });
    },

    /** One entry dismissed from the panel. */
    notificationDismissed: (state, action) => {
      state.items = state.items.filter((item) => item.id !== action.payload);
    },

    /**
     * "Limpiar" takes what is over. A job still running stays: dropping it
     * would re-enable the button that launched it and invite a second one.
     */
    notificationsCleared: (state) => {
      state.items = state.items.filter((item) => !FINISHED_STATUSES.includes(item.status));
    },

    /**
     * Marks the running entries that have been silent for too long. Not a
     * failure: the job may be slow, and a late event still closes the row.
     */
    notificationsStaleChecked: (state, action) => {
      const now = action.payload;
      state.items.forEach((item) => {
        if (item.status !== NOTIFICATION_STATUS.RUNNING) return;
        if (now - (item.updatedAt ?? item.createdAt) <= STALLED_AFTER_MS) return;
        item.status = NOTIFICATION_STATUS.STALLED;
        item.message = STATUS_MESSAGES[NOTIFICATION_STATUS.STALLED];
        item.read = false;
      });
    },

    activityPanelOpened: (state) => {
      state.isPanelOpen = true;
      state.items.forEach((item) => {
        item.read = true;
      });
    },

    activityPanelClosed: (state) => {
      state.isPanelOpen = false;
    },

    socketConnectionChanged: (state, action) => {
      state.isConnected = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Any thunk that opened an entry and then failed closes it, by request id.
    // No list of thunks: an export added tomorrow gets this for free.
    builder.addMatcher(isRejected, (state, action) => {
      const requestId = action.meta?.requestId;
      if (!requestId) return;

      const entry = state.items.find(
        (item) => item.requestId === requestId && item.status === NOTIFICATION_STATUS.RUNNING
      );
      if (!entry) return;

      const unconfirmed = action.meta?.unconfirmed === true;
      const status = unconfirmed ? NOTIFICATION_STATUS.UNCONFIRMED : NOTIFICATION_STATUS.ERROR;
      entry.status = status;
      entry.message = unconfirmed
        ? STATUS_MESSAGES[NOTIFICATION_STATUS.UNCONFIRMED]
        : (typeof action.payload === 'string' && action.payload) ||
          NOTIFICATION_KINDS[entry.kind]?.error ||
          'No se pudo iniciar.';
      entry.updatedAt = Date.now();
      entry.read = false;
    });
  },
});

export const {
  notificationStarted,
  notificationReceived,
  notificationsMarkAllRead,
  notificationDismissed,
  notificationsCleared,
  notificationsStaleChecked,
  activityPanelOpened,
  activityPanelClosed,
  socketConnectionChanged,
} = notificationsSlice.actions;

export const selectNotifications = (state) => state.notifications.items;

export const selectUnreadCount = (state) =>
  state.notifications.items.reduce((total, item) => (item.read ? total : total + 1), 0);

/** Whether a job of this kind is in flight, for the button that launches it. */
export const selectIsKindRunning = (kind) => (state) =>
  state.notifications.items.some(
    (item) => item.kind === kind && item.status === NOTIFICATION_STATUS.RUNNING
  );

/** Whether any job is still running, for the watchdog. */
export const selectHasRunningJobs = (state) =>
  state.notifications.items.some((item) => item.status === NOTIFICATION_STATUS.RUNNING);

/**
 * Whether any job is still waiting for the socket to say how it ended —
 * running, stalled or unconfirmed — for the header's offline warning. A job
 * marked stalled is exactly the one a dead socket would explain.
 */
export const selectHasOpenJobs = (state) =>
  state.notifications.items.some((item) => OPEN_STATUSES.includes(item.status));

export const selectIsActivityPanelOpen = (state) => state.notifications.isPanelOpen;

export const selectIsSocketConnected = (state) => state.notifications.isConnected;

export default persistReducer(notificationsPersistConfig, notificationsSlice.reducer);
